// What a kept ticket's legs are worth NOW, beside what they were worth when
// the pad printed them.
//
// A ticket is a record: the price on it is the price that was offered, and
// nothing in here ever rewrites it. This module only answers the second
// question a punter asks of an old ticket — "and what is it now?" — with one
// of exactly two real numbers:
//
//   'board'  the same bookmaker's price for the same side, still on today's
//            board. The match has not started.
//   'close'  the closing price, once the match has one. A closed match has no
//            "now" left, so the close is the last word and outranks the board.
//
// There is no third answer. A leg whose match is off the board and has no
// closing row returns null, and the ticket prints nothing extra rather than
// implying the price held.
//
// `openingPriceFor` answers a third question — "and what did it open at?" — off
// the same board, from the opening the server stamps onto each price row (F1,
// server/odds-series.mjs). It is context and nothing else: no strategy, no
// stake and no gate reads it.
//
// Pure: no refs, no storage, no clock. `pickClosing` is imported rather than
// reimplemented so a leg is credited with the same closing row here as it is
// when the paper trail settles it — two opinions about which book closed a leg
// would be two different CLVs.

import { priceAt } from './engine/universe.js';
import { pickClosing } from './stores/settlement.js';

/** No live price, and no pretending otherwise. */
export const NO_LIVE = Object.freeze({ live: null, source: null });

const cents = (n) => Math.round(n * 100) / 100;

/**
 * The bookmaker a leg's price was read at: the engine writes `bookmakerKey`,
 * a legacy Classic leg carries only the pad's own book, and `fallback` is the
 * risk profile's. A leg is always re-priced at ITS OWN book — the drift a
 * punter cares about is in the price they could have taken, not in what some
 * other book would have paid.
 */
export const bookmakerFor = (leg, fallback = null) =>
  leg?.bookmakerKey ?? leg?.bookmaker ?? fallback ?? null;

/**
 * The leg's match on the board, under either name it can carry: the engine
 * stamps `matchKey`, and a Classic leg only ever had the provider's event id.
 * Match keys are asked first, so a provider id that happens to read like
 * somebody else's key cannot repoint a leg at the wrong game — the same order
 * `lib/sets.js` indexes the board in.
 *
 * Exported because `lib/margin.js` has to find the same match for the same leg:
 * a second opinion about which fixture a leg belongs to would price the margin
 * off a different game.
 */
export function matchFor(universe, leg) {
  const matches = universe?.matches;
  if (!matches?.values || !leg) return null;
  if (leg.matchKey) {
    const byKey = matches.get?.(leg.matchKey);
    if (byKey) return byKey;
  }
  const id = leg.eventId ?? leg.matchKey;
  if (!id) return null;
  for (const match of matches.values()) {
    if (match?.providerEventId === id) return match;
  }
  return null;
}

/**
 * One leg's live price: `{ live, source }`, with `source` 'board', 'close' or
 * null.
 *
 * `closingRows` is a day of closing rows (the shape `/api/closing` returns);
 * a leg that has already been settled carries its own `closingPrice`, which is
 * the same number by the time it is written. Either one is a close.
 */
export function livePriceFor(leg, { universe = null, bookmaker = null, closingRows = null } = {}) {
  if (!leg) return NO_LIVE;

  const closed = pickClosing(closingRows, leg);
  if (closed && Number.isFinite(closed.price)) return { live: closed.price, source: 'close' };
  if (Number.isFinite(leg.closingPrice)) return { live: leg.closingPrice, source: 'close' };

  const match = matchFor(universe, leg);
  if (!match) return NO_LIVE;
  const book = bookmakerFor(leg, bookmaker);
  if (!book || !leg.side) return NO_LIVE;
  const now = priceAt(match, leg.side, book);
  return Number.isFinite(now) ? { live: now, source: 'board' } : NO_LIVE;
}

/**
 * How far this leg's price has come since the board opened: `{ opening, at,
 * move }`, or null.
 *
 * `move` is `price / opening − 1` at the leg's OWN book and on its own side —
 * the same rule `livePriceFor` follows, and for the same reason: the drift a
 * punter cares about is in the price they could have taken, not in what some
 * other shop did. The opening rides on the board (`buildUniverse`), stamped
 * server-side off the day's drift series (F1), so this costs no call.
 *
 * NULL RATHER THAN ZERO wherever the question cannot be answered: a board with
 * no openings on it (every board stored before the series existed), a leg whose
 * match has left the day, a book that was not quoting at the open. Context, and
 * never a rule: nothing in the engine reads this.
 */
export function openingPriceFor(leg, universe = null) {
  if (!leg || !Number.isFinite(leg.price)) return null;
  const match = matchFor(universe, leg);
  if (!match || !leg.side) return null;
  const book = bookmakerFor(leg);
  const row = match.prices?.[leg.side]?.find((r) => r.bookmaker === book);
  const opening = row?.opening;
  if (!Number.isFinite(opening) || opening <= 1) return null;
  return { opening, at: row.openingAt ?? null, move: leg.price / opening - 1 };
}

/** A resolver bound to one board: `(leg) => { live, source }`. */
export const liveResolver = (options) => (leg) => livePriceFor(leg, options);

/** `{ live, source }` per leg, aligned with `legs` — what `<Ticket live>` takes. */
export const livePricesFor = (legs, options) => (legs ?? []).map((leg) => livePriceFor(leg, options));

/** Which word a live price takes on the paper: where it was read. */
const SOURCE_WORD = { board: 'now', close: 'close' };

/**
 * One leg's move — `{ live, word }` — or null when there is nothing to print.
 *
 * ROUNDED TO CENTS, NOT TO A STRING. Both tickets wrote this for themselves and
 * both compared `price(a) === price(b)`, which asks the formatter a question
 * about arithmetic: it is right until the day one of them prints a price some
 * other way, and then the two stubs disagree about whether the same leg moved.
 * A price IS cents, so cents is what is compared, and a leg at 1.2999999
 * against 1.30 is a leg that has not moved.
 */
export function priceShift(printed, live, source = null) {
  if (!Number.isFinite(printed) || !Number.isFinite(live)) return null;
  if (cents(printed) === cents(live)) return null;
  return { live, word: SOURCE_WORD[source] ?? 'now' };
}

/**
 * What the whole multi is worth now: every leg's live price where it has one,
 * its own printed price where it does not, multiplied out and rounded to cents
 * the same way `combinedOdds` was.
 *
 * Null when any leg has neither — a multi missing one of its legs is not a
 * shorter multi, it is an unanswerable question.
 */
export function liveCombined(legs, resolver) {
  const list = legs ?? [];
  if (!list.length) return null;
  let product = 1;
  for (const leg of list) {
    const { live } = resolver?.(leg) ?? NO_LIVE;
    const p = Number.isFinite(live) ? live : leg?.price;
    if (!Number.isFinite(p)) return null;
    product *= p;
  }
  return cents(product);
}

/**
 * Which word the combined line takes: 'close' only when every leg that has a
 * live price got it from a closing row, otherwise 'now'. A half-closed multi
 * is still moving, and calling it closed would be the ticket claiming the day
 * is over when one of its matches has not started.
 */
export function liveSource(legs, resolver) {
  let any = false;
  let allClosed = true;
  for (const leg of legs ?? []) {
    const { source } = resolver?.(leg) ?? NO_LIVE;
    if (!source) continue;
    any = true;
    if (source !== 'close') allClosed = false;
  }
  if (!any) return null;
  return allClosed ? 'close' : 'now';
}

/**
 * A day of settled proposals (what `stores/paper-trail.js` holds under a date)
 * → the closing rows `pickClosing` reads.
 *
 * The paper trail keeps the closing PRICE on each leg, not the rows it came
 * from, so this turns them back into rows rather than teaching the components
 * a second way to find a close. The book is the one the leg was taken at,
 * which is the book `pickClosing` prefers anyway; a leg with no close at all
 * contributes no row.
 */
export function closingRowsFrom(dayProposals) {
  const rows = [];
  const seen = new Set();
  for (const proposal of dayProposals ?? []) {
    for (const leg of proposal?.legs ?? []) {
      if (!leg?.matchKey || !leg.side || !Number.isFinite(leg.closingPrice)) continue;
      const book = leg.bookmakerKey ?? null;
      const key = `${leg.matchKey}|${leg.side}|${book}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({ matchKey: leg.matchKey, selection: leg.side, bookmaker: book, price: leg.closingPrice });
    }
  }
  return rows;
}
